import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import API_BASE from "../config";
import AlbumService from "../services/album.service";
import Spinner from "./Spinner";

// Create / edit an album. Supports direct multi-file image upload (drag the
// files or pick them), captioning, removing and reordering images.
const AlbumForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const fileInput = useRef(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [published, setPublished] = useState(true);

  // Existing (persisted) images: { id, url, caption, _remove }
  const [existing, setExisting] = useState([]);
  // New picks: { file, preview, caption }
  const [newFiles, setNewFiles] = useState([]);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const resolveMsg = (err) =>
    (err.response && err.response.data && err.response.data.message) || err.message;

  useEffect(() => {
    if (!isEdit) return;
    AlbumService.get(id)
      .then((res) => {
        const a = res.data;
        setTitle(a.title || "");
        setDescription(a.description || "");
        setPublished(!!a.published);
        setExisting((a.images || []).map((img) => ({ ...img, _remove: false })));
      })
      .catch((err) => {
        toast.error(resolveMsg(err));
        navigate("/manage-albums");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Revoke object URLs on unmount.
  useEffect(() => {
    return () => newFiles.forEach((f) => URL.revokeObjectURL(f.preview));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (fileList) => {
    const picked = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (picked.length === 0) return;
    setNewFiles((prev) => [
      ...prev,
      ...picked.map((file) => ({ file, preview: URL.createObjectURL(file), caption: "" })),
    ]);
  };

  const handleSelect = (e) => {
    addFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeNew = (idx) => {
    setNewFiles((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[idx].preview);
      next.splice(idx, 1);
      return next;
    });
  };

  const toggleRemoveExisting = (imgId) =>
    setExisting((prev) =>
      prev.map((img) => (img.id === imgId ? { ...img, _remove: !img._remove } : img))
    );

  const moveExisting = (idx, dir) => {
    setExisting((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const setExistingCaption = (imgId, caption) =>
    setExisting((prev) => prev.map((img) => (img.id === imgId ? { ...img, caption } : img)));

  const setNewCaption = (idx, caption) =>
    setNewFiles((prev) => prev.map((f, i) => (i === idx ? { ...f, caption } : f)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error(t("album.titleRequired", "Title is required."));
      return;
    }

    const kept = existing.filter((img) => !img._remove);
    if (kept.length === 0 && newFiles.length === 0) {
      toast.error(t("album.needImage", "Add at least one image."));
      return;
    }

    setSaving(true);
    setProgress(0);

    const fields = {
      title: title.trim(),
      description,
      published,
    };

    const files = newFiles.map((f) => f.file);
    // Note: new files are appended after existing ones server-side; existing
    // order + captions are sent so staff can reorder/caption kept images.
    fields.order = kept.map((img) => img.id);
    fields.removeImageIds = existing.filter((img) => img._remove).map((img) => img.id);
    fields.captions = kept.reduce((acc, img) => {
      acc[img.id] = img.caption || "";
      return acc;
    }, {});
    // Captions for new uploads, aligned with `files` order.
    fields.newCaptions = newFiles.map((f) => f.caption || "");

    try {
      if (isEdit) {
        await AlbumService.update(id, fields, files, setProgress);
        toast.success(t("album.updated", "Album updated."));
      } else {
        await AlbumService.create(fields, files, setProgress);
        toast.success(t("album.created", "Album created."));
      }
      navigate("/manage-albums");
    } catch (err) {
      toast.error(resolveMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  const thumbStyle = { width: "100%", height: 130, objectFit: "cover", borderRadius: 6 };

  return (
    <div className="container mt-4" style={{ maxWidth: 920 }}>
      <h3 className="mb-3">
        {isEdit ? t("album.editAlbum", "Edit Album") : t("album.newAlbum", "New Album")}
      </h3>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">{t("album.fieldTitle", "Title")} *</label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">{t("album.fieldDescription", "Description")}</label>
          <textarea
            className="form-control"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="album-published"
            checked={published}
            onChange={(e) => setPublished(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="album-published">
            {t("album.publishedHint", "Published (visible in public gallery)")}
          </label>
        </div>

        {/* Upload dropzone */}
        <div
          className={`border rounded p-4 text-center mb-3 ${dragOver ? "border-primary bg-light" : "border-2 border-dashed"}`}
          style={{ cursor: "pointer", borderStyle: "dashed" }}
          onClick={() => fileInput.current && fileInput.current.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <div className="fw-semibold">{t("album.dropHere", "Drop images here or click to upload")}</div>
          <div className="small text-muted">
            {t("album.dropHint", "You can select multiple images (jpg, png, gif, webp — max 5MB each)")}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleSelect}
          />
        </div>

        {/* Existing images */}
        {existing.length > 0 && (
          <>
            <h6 className="text-muted">{t("album.currentImages", "Current images")}</h6>
            <div className="row g-3 mb-3">
              {existing.map((img, idx) => (
                <div className="col-6 col-md-3" key={img.id}>
                  <div className={`card h-100 ${img._remove ? "opacity-50 border-danger" : ""}`}>
                    <img src={API_BASE + img.url} alt={img.caption || ""} style={thumbStyle} />
                    <div className="card-body p-2">
                      <input
                        type="text"
                        className="form-control form-control-sm mb-2"
                        placeholder={t("album.caption", "Caption")}
                        value={img.caption || ""}
                        onChange={(e) => setExistingCaption(img.id, e.target.value)}
                        disabled={img._remove}
                      />
                      <div className="d-flex justify-content-between">
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => moveExisting(idx, -1)}
                            disabled={idx === 0 || img._remove}
                            title={t("album.moveLeft", "Move left")}
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => moveExisting(idx, 1)}
                            disabled={idx === existing.length - 1 || img._remove}
                            title={t("album.moveRight", "Move right")}
                          >
                            →
                          </button>
                        </div>
                        <button
                          type="button"
                          className={`btn btn-sm ${img._remove ? "btn-secondary" : "btn-outline-danger"}`}
                          onClick={() => toggleRemoveExisting(img.id)}
                        >
                          {img._remove ? t("common.undo", "Undo") : t("common.delete")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* New picks */}
        {newFiles.length > 0 && (
          <>
            <h6 className="text-muted">{t("album.newImages", "New images to upload")}</h6>
            <div className="row g-3 mb-3">
              {newFiles.map((f, idx) => (
                <div className="col-6 col-md-3" key={idx}>
                  <div className="card h-100">
                    <img src={f.preview} alt="" style={thumbStyle} />
                    <div className="card-body p-2">
                      <input
                        type="text"
                        className="form-control form-control-sm mb-2"
                        placeholder={t("album.caption", "Caption")}
                        value={f.caption}
                        onChange={(e) => setNewCaption(idx, e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger w-100"
                        onClick={() => removeNew(idx)}
                      >
                        {t("common.remove", "Remove")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {saving && progress > 0 && (
          <div className="progress mb-3" style={{ height: 6 }}>
            <div className="progress-bar" role="progressbar" style={{ width: `${progress}%` }} />
          </div>
        )}

        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t("common.saving", "Saving…") : t("common.save")}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate("/manage-albums")}
            disabled={saving}
          >
            {t("common.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AlbumForm;
